import { create } from 'zustand';
import type { IOrgSummary, IProfile } from '@stem/contracts';
import { can, canAny } from '@stem/contracts';

interface SessionState {
  profile: IProfile | null;
  orgs: IOrgSummary[];
  /** The org the current org token is scoped to. */
  activeOrg: IOrgSummary | null;
  permissions: string[];
  entitlements: string[];
  ready: boolean;

  setSession: (profile: IProfile, orgs: IOrgSummary[]) => void;
  setOrgContext: (org: IOrgSummary, permissions: string[], entitlements: string[]) => void;
  setProfile: (profile: IProfile) => void;
  setReady: (ready: boolean) => void;
  reset: () => void;
}

/**
 * Everything the shell needs to render its first frame.
 *
 * `permissions` and `entitlements` come from the org-token response, which is
 * why switching schools re-populates them in one round trip rather than three.
 * They are used only to hide controls the guards would refuse anyway — the
 * server checks every one of them again, so a stale list here is a cosmetic
 * problem, never a security one.
 */
export const useSession = create<SessionState>(set => ({
  profile: null,
  orgs: [],
  activeOrg: null,
  permissions: [],
  entitlements: [],
  ready: false,

  setSession: (profile, orgs) => set({ profile, orgs }),
  setOrgContext: (activeOrg, permissions, entitlements) =>
    set({ activeOrg, permissions, entitlements }),
  setProfile: profile => set({ profile }),
  setReady: ready => set({ ready }),
  reset: () =>
    set({
      profile: null,
      orgs: [],
      activeOrg: null,
      permissions: [],
      entitlements: [],
      ready: true,
    }),
}));

/** `usePermission('course@publish')` — hides a button the guard would refuse. */
export function usePermission(...required: string[]): boolean {
  return useSession(state => can(state.permissions, ...required));
}

export function useAnyPermission(...candidates: string[]): boolean {
  return useSession(state => canAny(state.permissions, ...candidates));
}

/**
 * Whether the acting member's plan includes a feature.
 *
 * Used to show an upgrade prompt *instead of* the locked thing, rather than to
 * hide it. The outline's whole commercial model is a free tier that shows you
 * what the paid tier has; hiding the shelf sells nothing.
 */
export function useEntitlement(feature: string): boolean {
  return useSession(state => state.entitlements.includes(feature));
}
